import { ProfileTypes } from "@/appComponents/Blocks/Cards/ProfileBaseballCard/ProfileBaseballCard.utils";
import { AgentProfileSettings } from "@/appComponents/Blocks/Cards/ProfileBaseballCard/Agent/AgentProfile.constants";
import {
  enumObjClassNames,
  kDivisionID,
  kEmailID,
  kEmployeeID,
  kEmployeeLocation,
  kHeadcount,
  kLevelID,
  kLocationID,
  kNameID,
  kPeersID,
  kPersonID,
  kPhoneID,
  kPositionDivisionID,
  kPositionEndDate,
  kPositionID,
  kPositionLocation,
  kPositionStartDate,
  kPositionTitleID,
  kTitleID
} from "@coreapp/constants";
import { QXDoc } from "@coreapp/CoreApp/QPlanManager";
import { QProfileMgr } from "@coreapp/CoreApp/QProfileMgr";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import { appstr } from "@coreapp/utils/appstr";
import {
  ChartSettingsSectionRoutes,
  getChartSettingsTestPrefix
} from "@/providers/NavigationProvider";

export const PROFILE_TEST_PREFIX = getChartSettingsTestPrefix(ChartSettingsSectionRoutes.Profile);

export enum FieldSwapAction {
  Include = "include"
}

export enum FieldSwapScope {
  Single = "single"
}

export type EmployeeFieldConfig = {
  showProfile: boolean;
  phone: string;
  location: string;
  email: string;
  favoriteField1: string;
  favoriteField2: string;
  favoriteField3: string;
};

export type PositionFieldConfig = {
  workLocation: string;
  positionStartDate: string;
  positionEndDate: string;
};

export type HeadingConfigType = {
  [ProfileTypes.Employee]: EmployeeFieldConfig;
  [ProfileTypes.Position]: PositionFieldConfig;
  [ProfileTypes.Agent]?: AgentProfileSettings;
};

export type RequestHeadingFieldSwapFn = (
  fieldName: string,
  onConfirm: () => void,
  action?: FieldSwapAction,
  scope?: FieldSwapScope
) => void;

type FieldsSwapModalContent = {
  title: string;
  message: string;
  question: string;
  confirmLabel: string;
};

const FIELDS_SWAP_SETTINGS_DISPLAY: Record<
  FieldSwapAction,
  Record<FieldSwapScope, (fieldName: string) => FieldsSwapModalContent>
> = {
  [FieldSwapAction.Include]: {
    [FieldSwapScope.Single]: fieldName => ({
      title: appstr("Include field?"),
      message: appstr(
        'The "{fieldName}" field is currently excluded from the profile. If you map this field, it will be included in the profile.',
        { fieldName }
      ),
      question: appstr("Are you sure you want to include this field?"),
      confirmLabel: appstr("Include field")
    })
  }
};

export const getHeadingSwapContent = (
  fieldName: string,
  action: FieldSwapAction = FieldSwapAction.Include,
  scope: FieldSwapScope = FieldSwapScope.Single
): FieldsSwapModalContent => {
  return FIELDS_SWAP_SETTINGS_DISPLAY[action][scope](fieldName);
};

export const isProfileFieldExcluded = (
  fieldName: string,
  config: any,
  type: ProfileTypes
): boolean => {
  const shownFields = config?.profile?.shownFields;
  const legacyPositionFields = config?.profile?.shownPositionFields;
  const includedFields =
    type === ProfileTypes.Employee && Array.isArray(shownFields)
      ? shownFields
      : (shownFields?.[type]?.Included ??
        (type === ProfileTypes.Position && Array.isArray(legacyPositionFields)
          ? legacyPositionFields
          : undefined));
  if (!includedFields) return false;
  return !includedFields.includes(fieldName);
};

export const ensureHeadingFieldIncluded = (
  profileMap: any,
  type: ProfileTypes,
  fieldValue: string,
  fields: string[]
): void => {
  if (!profileMap.shownFields?.[type]?.Included) {
    const legacyIncluded: string[] | undefined =
      type === ProfileTypes.Employee
        ? Array.isArray(profileMap.shownFields)
          ? profileMap.shownFields
          : undefined
        : Array.isArray(profileMap.shownPositionFields)
          ? profileMap.shownPositionFields
          : undefined;

    const seed = legacyIncluded ?? [...fields];

    profileMap.shownFields = {
      ...(Array.isArray(profileMap.shownFields) ? {} : (profileMap.shownFields ?? {})),
      [type]: { Included: seed.includes(fieldValue) ? seed : [...seed, fieldValue] }
    };
  } else if (!profileMap.shownFields[type].Included.includes(fieldValue)) {
    profileMap.shownFields[type].Included = [...profileMap.shownFields[type].Included, fieldValue];
  }
};

const getFixedHeadingFields = (
  mapping: any,
  isPositionChart: boolean,
  type: ProfileTypes
): string[] => {
  if (type === ProfileTypes.Employee) {
    return [
      mapping[kNameID],
      mapping[isPositionChart ? kEmployeeID : kPersonID],
      mapping[kTitleID],
      mapping[kDivisionID]
    ];
  } else {
    return [mapping[kPositionTitleID], mapping[kPositionID], mapping[kPositionDivisionID]];
  }
};

export const getFieldsFromConfig = (config: any, type: ProfileTypes): string[] => {
  const headingConfig = config?.profile?.headingConfig?.[type];
  const mapping = config?.fields || {};
  const isPositionChart = IsPositionChart(config);

  const fixedHeadingFields = getFixedHeadingFields(mapping, isPositionChart, type);

  // Legacy handling
  if (!headingConfig) {
    if (type === ProfileTypes.Employee) {
      const usedDefaultConfig: any = getDefaultEmployeeFieldConfig(isPositionChart, mapping);
      delete usedDefaultConfig["showProfile"];

      if (config?.profile?.starFields) {
        usedDefaultConfig["favoriteField1"] = config?.profile?.starFields[0] || "";
        usedDefaultConfig["favoriteField2"] = config?.profile?.starFields[1] || "";
        usedDefaultConfig["favoriteField3"] = config?.profile?.starFields[2] || "";
      }

      return Object.values(usedDefaultConfig)
        .concat(fixedHeadingFields)
        .filter(Boolean) as string[];
    } else {
      const usedDefaultConfig: any = getDefaultPositionFieldConfig(mapping);
      return Object.values(usedDefaultConfig)
        .concat(fixedHeadingFields)
        .filter(Boolean) as string[];
    }
  }

  if (type === ProfileTypes.Employee) {
    return [
      ...fixedHeadingFields,
      headingConfig?.phone,
      headingConfig?.location,
      headingConfig?.email,
      headingConfig?.favoriteField1,
      headingConfig?.favoriteField2,
      headingConfig?.favoriteField3
    ].filter((f: string) => f && f.length > 0);
  } else {
    return [
      ...fixedHeadingFields,
      headingConfig?.workLocation,
      headingConfig?.positionStartDate,
      headingConfig?.positionEndDate
    ].filter((f: string) => f && f.length > 0);
  }
};

export const getDefaultEmployeeFieldConfig = (
  isPositionChart: boolean,
  mapping: Record<string, string>
): EmployeeFieldConfig => {
  const defaultConfigIDs: EmployeeFieldConfig = {
    showProfile: true,
    phone: mapping[kPhoneID] ?? "",
    location: mapping[isPositionChart ? kEmployeeLocation : kLocationID] ?? "",
    email: mapping[kEmailID] ?? "",
    // TODO: Change it to holder fields (when they exist).
    favoriteField1: isPositionChart ? "" : kLevelID,
    favoriteField2: isPositionChart ? "" : kHeadcount,
    favoriteField3: isPositionChart ? "" : kPeersID
  };

  return defaultConfigIDs;
};

export const getDefaultPositionFieldConfig = (
  mapping: Record<string, string>
): PositionFieldConfig => ({
  workLocation: mapping[kPositionLocation] ?? "",
  positionStartDate: mapping[kPositionStartDate] ?? "",
  positionEndDate: mapping[kPositionEndDate] ?? ""
});

export const updateProfileState = (data: any, pm: QProfileMgr, doc: QXDoc) => {
  // Use the newer version of Profile, or else the legacy array one.
  const shownBoxFields =
    data.profile.shownFields?.employee?.Included ||
    (Array.isArray(data.profile.shownFields) ? data.profile.shownFields : []);
  const shownPositionFields =
    data.profile.shownFields?.position?.Included ||
    (Array.isArray(data.profile.shownPositionFields) ? data.profile.shownPositionFields : []);

  const defaultFavorites = data.profile.starFields ?? [
    data.profile?.headingConfig?.employee?.favoriteField1,
    data.profile?.headingConfig?.employee?.favoriteField2,
    data.profile?.headingConfig?.employee?.favoriteField3
  ];

  pm.AddKeyFields(doc, enumObjClassNames.box, shownBoxFields);
  // Agent settings (INS-7) live entirely inside headingConfig.agent, so the
  // wholesale UpdateHeadingConfig replace live-applies them too.
  pm.UpdateHeadingConfig(data.profile.headingConfig);
  pm.SetDefaultStarFields(doc, defaultFavorites);
  pm.AddKeyFields(doc, enumObjClassNames.position, shownPositionFields);
};
